import React from 'react';
import {
  Box,
  Container,
  Typography,
  useMediaQuery,
  alpha,
  Card,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import { motion } from 'framer-motion';

const steps = [
  {
    step: 1,
    title: 'Search & Select Doctor',
    description: 'Browse through our extensive list of verified doctors and select the one that best fits your needs.',
    icon: SearchIcon,
    color: 'primary'
  },
  {
    step: 2,
    title: 'Book Appointment Instantly',
    description: 'Choose your preferred date and time slot, and book your appointment with just a few clicks.',
    icon: EventAvailableIcon,
    color: 'secondary'
  },
  {
    step: 3,
    title: 'Visit the Clinic',
    description: 'Visit the clinic at your scheduled time and receive quality medical care from your chosen doctor.',
    icon: LocalHospitalIcon,
    color: 'primary'
  }
];

const HowItWorks = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <Box 
      component="section"
      sx={{ 
        pt: { xs: 10, md: 15 },
        pb: { xs: 12, md: 18 },
        bgcolor: theme => alpha(theme.palette.background.default, 0.8),
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Background decoration */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '100%',
          opacity: 0.06,
          background: theme => `
            radial-gradient(circle at 20% 20%, ${theme.palette.primary.main} 0%, transparent 35%),
            radial-gradient(circle at 80% 80%, ${theme.palette.secondary.main} 0%, transparent 35%)
          `,
          zIndex: 0
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={containerVariants}
        >
          <Box sx={{ textAlign: 'center', mb: { xs: 8, md: 12 } }}>
            <motion.div variants={itemVariants}>
              <Typography 
                component="span"
                sx={{
                  display: 'inline-block',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: 'primary.main',
                  bgcolor: theme => alpha(theme.palette.primary.main, 0.1),
                  px: 3,
                  py: 1,
                  borderRadius: '100px',
                  mb: 3,
                  letterSpacing: '0.5px'
                }}
              >
                HOW IT WORKS
              </Typography>
              
              <Typography 
                variant="h2"
                sx={{
                  mb: 3,
                  fontWeight: 700,
                  background: theme => `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  textAlign: 'center'
                }}
              >
                Book Your Appointment in 3 Easy Steps
              </Typography>
              <Typography 
                variant="large"
                sx={{
                  color: 'theme.palette.primary.main',
                  maxWidth: '600px',
                  mx: 'auto',
                  fontWeight: 400,
                  lineHeight: 1.6
                }}
              >
                Get started with our simple three-step process to book your medical appointment
              </Typography>
            </motion.div>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              gap: { xs: 4, md: 5 },
              position: 'relative'
            }}
          >
            {steps.map((step, index) => (
              <motion.div
                key={step.step}
                variants={itemVariants}
              >
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    p: 4,
                    borderRadius: 4,
                    background: theme => alpha(theme.palette.background.paper, 0.8),
                    backdropFilter: 'blur(8px)',
                    border: '1px solid',
                    borderColor: theme => alpha(theme.palette[step.color].main, 0.1),
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: theme => `0 12px 24px ${alpha(theme.palette[step.color].main, 0.1)}`,
                      borderColor: theme => alpha(theme.palette[step.color].main, 0.2),
                    }
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%'
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: 3
                      }}
                    >
                      <Box
                        sx={{
                          width: 64,
                          height: 64,
                          borderRadius: 2,
                          bgcolor: theme => alpha(theme.palette[step.color].main, 0.1),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: `${step.color}.main`,
                          mr: 2
                        }}
                      >
                        <step.icon sx={{ fontSize: 28 }} />
                      </Box>
                      <Typography
                        variant="larger"
                        sx={{
                          color: `${step.color}.main`,
                          fontWeight: 600
                        }}
                      >
                        Step {step.step}
                      </Typography>
                    </Box>

                    <Typography
                      variant="large"
                      sx={{
                        mb: 2,
                        fontWeight: 600,
                        color: 'text.primary'
                      }}
                    >
                      {step.title}
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{
                        color: 'theme.palette.secondary.dark',
                        lineHeight: 1.7,
                        flex: 1
                      }}
                    >
                      {step.description}
                    </Typography>
                  </Box>
                </Card>
              </motion.div>
            ))}
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default HowItWorks; 